import React, { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Mail, Users, UserPlus, X, Send, Plus, Clock } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';

export const DigestSettingsCard = () => {
    const [config, setConfig] = useState({ enabled: false, recipients: [], include_ceo_coo: true, send_hour: 8 });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [history, setHistory] = useState([]);
    const [availableRecipients, setAvailableRecipients] = useState([]);
    const [showPicker, setShowPicker] = useState(false);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        Promise.all([
            api.get('/digest/config').then(r => setConfig(r.data.config || r.data)).catch(e => logger.warn('Digest config error', e)),
            api.get('/digest/history').then(r => setHistory(r.data.digests || [])).catch(e => logger.warn('Digest history error', e)),
            api.get('/users', { params: { limit: 200 } }).then(r => setAvailableRecipients(r.data.users || r.data || [])).catch(e => logger.warn('Recipients error', e)),
        ]).finally(() => setLoading(false));
    }, []);

    const filteredRecipients = useMemo(() =>
        availableRecipients.filter(u => !(config.recipients || []).some(r => r.user_id === u.id)),
        [availableRecipients, config.recipients]
    );

    const handleToggle = async (enabled) => {
        setSaving(true);
        try {
            const res = await api.put('/digest/config', { enabled });
            setConfig(res.data.config);
            toast.success(enabled ? 'Weekly digest enabled' : 'Weekly digest disabled');
        } catch { toast.error('Failed to update digest settings'); }
        finally { setSaving(false); }
    };

    const handleAddRecipient = async (u) => {
        const current = config.recipients || [];
        if (current.some(r => r.user_id === u.id)) return;
        const updated = [...current, { user_id: u.id, email: u.email, name: u.full_name, role: u.role }];
        setSaving(true);
        try {
            const res = await api.put('/digest/config', { recipients: updated });
            setConfig(res.data.config);
            toast.success(`Added ${u.full_name}`);
        } catch { toast.error('Failed to add recipient'); }
        finally { setSaving(false); }
    };

    const handleRemoveRecipient = async (userId) => {
        const updated = (config.recipients || []).filter(r => r.user_id !== userId);
        setSaving(true);
        try {
            const res = await api.put('/digest/config', { recipients: updated });
            setConfig(res.data.config);
            toast.success('Recipient removed');
        } catch { toast.error('Failed to remove recipient'); }
        finally { setSaving(false); }
    };

    const handleSendNow = async () => {
        setSending(true);
        try {
            const res = await api.post('/digest/send-now');
            toast.success(res.data.message);
        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to trigger digest'); }
        finally { setSending(false); }
    };

    if (loading) return null;

    return (
        <Card className={`border-2 transition-colors ${config.enabled ? 'border-indigo-500/50 bg-indigo-500/5' : 'border-muted'}`} data-testid="digest-settings-card">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Mail className="h-5 w-5 text-indigo-500" />
                        <CardTitle>Weekly Coaching Digest</CardTitle>
                        {config.enabled
                            ? <Badge className="bg-indigo-500 text-white text-[10px]">Active</Badge>
                            : <Badge variant="outline" className="text-muted-foreground text-[10px]">Off</Badge>
                        }
                    </div>
                    <div className="flex items-center gap-3">
                        {saving && <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />}
                        <Switch checked={config.enabled} onCheckedChange={handleToggle} disabled={saving} data-testid="digest-master-toggle" />
                    </div>
                </div>
                <CardDescription>
                    AI-powered weekly email digest with per-team coaching, cross-team insights, and objection handling tips from NLP call data.
                    <span className="block mt-1 text-xs text-muted-foreground">Sends every Monday morning (8 AM UAE). CEO/COO get company-wide view; Team Leaders get their team + cross-team suggestions.</span>
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Recipients */}
                <div className="p-4 rounded-lg bg-background border">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-indigo-500" />
                            <span className="text-sm font-medium">Recipients</span>
                            <Badge variant="outline" className="text-[10px]">
                                {(config.recipients?.length || 0) + (config.include_ceo_coo ? ' + CEO/COO auto' : '')}
                            </Badge>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => setShowPicker(!showPicker)} data-testid="add-recipient-btn">
                            <UserPlus className="h-3.5 w-3.5 mr-1" /> Add
                        </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                        {config.include_ceo_coo && (
                            <Badge className="bg-indigo-500/15 text-indigo-400 border border-indigo-500/30 text-xs">CEO/COO (auto)</Badge>
                        )}
                        {(config.recipients || []).map((r) => (
                            <Badge key={r.user_id} variant="outline" className="text-xs flex items-center gap-1 pr-1">
                                {r.name} <span className="text-muted-foreground">({r.role?.replace(/_/g, ' ')})</span>
                                <button className="ml-1 hover:text-red-500 transition-colors" onClick={() => handleRemoveRecipient(r.user_id)}>
                                    <X className="h-3 w-3" />
                                </button>
                            </Badge>
                        ))}
                    </div>
                    {showPicker && (
                        <div className="mt-2 border rounded-lg p-2 max-h-48 overflow-y-auto bg-muted/30 space-y-1" data-testid="recipient-picker">
                            {filteredRecipients.map(u => (
                                <button key={u.id} className="w-full flex items-center justify-between text-xs p-2 rounded hover:bg-muted transition-colors text-left" onClick={() => handleAddRecipient(u)}>
                                    <span>{u.full_name} <span className="text-muted-foreground">({u.role?.replace(/_/g, ' ')})</span></span>
                                    <Plus className="h-3 w-3 text-indigo-500" />
                                </button>
                            ))}
                            {filteredRecipients.length === 0 && (
                                <p className="text-xs text-muted-foreground text-center py-2">All eligible users added</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Manual Send + History */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-background border">
                        <div className="flex items-center gap-2 mb-2">
                            <Send className="h-4 w-4 text-indigo-500" />
                            <span className="text-sm font-medium">Send Now</span>
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">Manually trigger a digest email with this week's data (ignores schedule).</p>
                        <Button size="sm" variant="outline" onClick={handleSendNow} disabled={sending} className="border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10" data-testid="send-digest-now-btn">
                            {sending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                            {sending ? 'Sending...' : 'Send Digest Now'}
                        </Button>
                    </div>
                    <div className="p-4 rounded-lg bg-background border">
                        <div className="flex items-center gap-2 mb-2">
                            <Clock className="h-4 w-4 text-indigo-500" />
                            <span className="text-sm font-medium">Recent Digests</span>
                        </div>
                        {history.length === 0 ? (
                            <p className="text-xs text-muted-foreground">No digests sent yet</p>
                        ) : (
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                                {history.map((d) => (
                                    <div key={d.id} className="flex items-center justify-between text-[10px] py-1 border-b border-muted last:border-0">
                                        <span>{new Date(d.generated_at).toLocaleDateString()}</span>
                                        <span className="text-muted-foreground">{d.total_calls} calls, {d.recipients_sent?.length || 0} sent</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-3 rounded-lg bg-muted/50 border text-xs text-muted-foreground space-y-1">
                    <p className="font-medium text-foreground">How the digest works:</p>
                    <p>1. Every Monday at 8 AM (UAE), the system gathers all NLP-analyzed calls from the past 7 days</p>
                    <p>2. Claude AI generates fresh insights: per-agent scores, cross-team patterns, objection handling techniques</p>
                    <p>3. CEO/COO receives a company-wide view with all teams' data and top performers</p>
                    <p>4. Team Leaders receive their team's agent breakdown + suggestions from what works in other teams</p>
                </div>
            </CardContent>
        </Card>
    );
};
